// What the surface's popover, dialog and sheet share: their open state, and focus management that
// stays inside the surface's own tree.
//
// Why the surface owns this rather than Radix's Popover and Dialog: their content always calls
// useFocusGuards, which inserts two <span data-radix-focus-guard> into document.body, and a modal
// Dialog also sets aria-hidden on the body's other children (aria-hidden's hideOthers), locks its
// scroll (react-remove-scroll, a <style> in <head> and attributes on <body>) and sets
// body.style.pointerEvents. Over an observed application every one of those is a write to its DOM
// outside the overlay root (AGENTS.md "Never"). Radix's focus scope also reads document.activeElement,
// which is the shadow host when focus is inside the overlay's shadow root, so it cannot tell where
// focus is there. Positioning (Radix's Popper) and dismissal (Radix's DismissableLayer, which only
// listens) are still Radix's.
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from "react";
export function useOpenState({ open, defaultOpen = false, onOpenChange, }) {
    const [own, setOwn] = useState(defaultOpen);
    const value = open ?? own;
    const latest = useRef({ value, onOpenChange, controlled: open !== undefined });
    latest.current = { value, onOpenChange, controlled: open !== undefined };
    const set = useCallback((next) => {
        const { value: now, onOpenChange: notify, controlled } = latest.current;
        if (next === now)
            return;
        if (!controlled)
            setOwn(next);
        notify?.(next);
    }, []);
    return [value, set];
}
/**
 * The element focused in the node's own tree. Inside a shadow root document.activeElement is the
 * shadow host, so the root's own activeElement is the one to read.
 */
export function focusedIn(node) {
    const root = node.getRootNode();
    return root instanceof Document || root instanceof ShadowRoot ? root.activeElement : null;
}
/** The elements inside the container that Tab reaches, in document order. */
export function tabbables(container) {
    const out = [];
    const walker = container.ownerDocument.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
        acceptNode(node) {
            const el = node;
            if (el.hidden || el.inert)
                return NodeFilter.FILTER_REJECT;
            const disabled = "disabled" in el && el.disabled;
            const hiddenInput = el instanceof HTMLInputElement && el.type === "hidden";
            return !disabled && !hiddenInput && el.tabIndex >= 0
                ? NodeFilter.FILTER_ACCEPT
                : NodeFilter.FILTER_SKIP;
        },
    });
    while (walker.nextNode())
        out.push(walker.currentNode);
    return out;
}
function focus(el) {
    el?.focus({ preventScroll: true });
}
const scopes = [];
/** Nothing has focus: what had it was removed, and the browser left focus on <body>. */
function focusLost(doc) {
    return doc.activeElement === null || doc.activeElement === doc.body;
}
/**
 * Focus for a layer's content element: the first control takes focus as it opens (or the content
 * itself if it has none), Tab and Shift+Tab wrap at its edges, a trapped layer pulls focus back, and
 * onUnmount runs as it closes. Every read goes through the content's own root node, so this works in
 * a shadow root; nothing is written outside the content. Returns the content's keydown handler.
 *
 * Nested layers (a menu opened inside a dialog or another menu): the inner one is the innermost
 * scope before it takes focus, so the outer one does not pull that focus back. When the inner one
 * closes without handing focus on (a press outside it, where its focused control goes with it and
 * nothing else takes focus), the innermost layer still open takes focus back rather than leave it
 * on <body>: to the inner one's opener (its trigger) when that is inside it. That is the innermost
 * open layer anywhere, not necessarily the closing one's parent; the two differ only when sibling
 * layers are held open at once through a controlled `open`. If that layer is a trap (a modal
 * dialog), it takes focus back from anywhere outside it as well: a menu in a dialog that closes
 * because the page focused an element of its own hands nothing on (the user moved on), and the
 * dialog, still open, would otherwise leave focus on the page. A layer that is not a trap leaves
 * focus that went elsewhere where it went (a lone menu, or a menu in a menu, closed by a click or a
 * focus() on the page).
 */
export function useFocusScope(content, { trapped, onUnmount }) {
    const unmount = useRef(onUnmount);
    unmount.current = onUnmount;
    useEffect(() => {
        if (content === null)
            return;
        const before = focusedIn(content);
        const opener = before instanceof HTMLElement && !content.contains(before) ? before : null;
        let last = null;
        const back = (prefer) => [prefer, last].find((el) => el != null && content.contains(el)) ??
            tabbables(content)[0] ??
            content;
        const scope = {
            trapped,
            holds: () => content.contains(focusedIn(content)),
            reclaim: (prefer) => {
                if (content.isConnected)
                    focus(back(prefer));
            },
        };
        scopes.push(scope);
        if (!(before !== null && content.contains(before)))
            focus(tabbables(content)[0] ?? content);
        const onFocusIn = () => {
            if (scopes.at(-1) !== scope)
                return;
            const now = focusedIn(content);
            if (now instanceof HTMLElement && content.contains(now))
                last = now;
            else if (trapped)
                focus(back(null));
        };
        // focusin is composed: a move into or out of a shadow root reaches the document, retargeted. A
        // move between two elements of one shadow root does not (both ends retarget to the host, and the
        // event stops at the root), so in a shadow root the scope listens on the root as well: a focus()
        // on another surface element behind a modal's scrim is pulled back like a move onto the page.
        // A move into the root from outside reaches both listeners, and the handler runs twice for it;
        // the second run finds focus already where the first put it and does nothing more.
        const doc = content.ownerDocument;
        const root = content.getRootNode();
        const shadow = root instanceof ShadowRoot ? root : null;
        doc.addEventListener("focusin", onFocusIn);
        shadow?.addEventListener("focusin", onFocusIn);
        return () => {
            doc.removeEventListener("focusin", onFocusIn);
            shadow?.removeEventListener("focusin", onFocusIn);
            scopes.splice(scopes.indexOf(scope), 1);
            unmount.current(opener);
            const next = scopes.at(-1);
            if (next !== undefined && (focusLost(doc) || (next.trapped && !next.holds()))) {
                next.reclaim(opener);
            }
        };
    }, [content, trapped]);
    return useCallback((event) => {
        if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey)
            return;
        const box = event.currentTarget;
        const list = tabbables(box);
        const first = list[0];
        const last = list.at(-1);
        const now = focusedIn(box);
        if (first === undefined || last === undefined) {
            event.preventDefault();
        }
        else if (!event.shiftKey && now === last) {
            event.preventDefault();
            focus(first);
        }
        else if (event.shiftKey && (now === first || now === box)) {
            event.preventDefault();
            focus(last);
        }
    }, []);
}
/** Runs the caller's handler, then ours unless the caller prevented the default. */
export function compose(theirs, ours) {
    return (event) => {
        theirs?.(event);
        if (!event.defaultPrevented)
            ours(event);
    };
}
/**
 * The popover a layer was opened from. A portal puts a nested layer (a menu inside a menu, or a
 * tooltip or dialog opened from one) elsewhere in the DOM than the popover it belongs to, so the
 * popover's press test, which reads the DOM, would take a press on it for a press outside and close
 * (and the nested layer with it, before its click lands). Each nested layer registers its elements
 * with every popover above it (useLayerPart); a press on them counts as inside.
 */
export const LayerParts = createContext(null);
/** Registers node with every popover this layer was opened from. */
export function useLayerPart(node) {
    const register = useContext(LayerParts);
    useEffect(() => (node === null || register === null ? undefined : register(node)), [node, register]);
}
/**
 * A popover's own parts: the elements of the layers opened from inside it, and the register its
 * LayerParts provider hands them (which registers each with the popovers above it too).
 */
export function useLayerParts() {
    const above = useContext(LayerParts);
    const [parts] = useState(() => new Set());
    const register = useCallback((node) => {
        parts.add(node);
        const undo = above?.(node);
        return () => {
            parts.delete(node);
            undo?.();
        };
    }, [parts, above]);
    return [parts, register];
}
/** One ref callback that sets every ref given. */
export function composeRefs(...refs) {
    return (node) => {
        for (const ref of refs) {
            if (typeof ref === "function")
                ref(node);
            else if (ref)
                ref.current = node;
        }
    };
}
//# sourceMappingURL=layer.js.map